import { useBreakpoint } from 'components/BreakpointProvider';
import { useSetModalTitleBackdrop } from 'components/remodal/v2/hooks';
import { mediaMMdMax } from 'helpers/breakpoints';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { FlexCol } from '../../primitives/flex';
import { useTrackComponentEvent } from './hooks/useTrackComponentEvent';

interface BookingSelectInputProps {
  defaultValue?: { label: string; value: string };
  options?: Array<{ label: string; value: string; available?: boolean }>;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  title?: string | null;
  name?: string;
  height?: string;
  error?: string | null;
  setter: (value: { label: string; value: string }) => void;
  modalVersion?: boolean;
  icon?: React.ReactNode;
  className?: string | null;
  isOwner?: boolean;
  notice?: React.ReactNode | null;
  object?: string;
  isMessageOwner?: boolean;
}

const BookingSelectInput = ({
  defaultValue: value,
  options = [],
  onChange = undefined,
  isDisabled = false,
  title = null,
  name,
  height = '80%',
  error,
  setter,
  modalVersion = false,
  icon = null,
  className = null,
  isOwner = false,
  notice = null,
  object = '',
  isMessageOwner = false,
}: BookingSelectInputProps) => {
  const setModalTitleBackDrop = useSetModalTitleBackdrop();
  const [isOpen, setIsOpen] = useState(false);

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const scrollDownRef = useRef<HTMLUListElement>();
  const mobileRef = useRef();

  useOnClickOutside(mobileRef, () => setIsOpen(false));

  const rudderTrack = useRudderStackTrack();
  const trackComponentEvent = useTrackComponentEvent();

  useEffect(() => {
    if (isOpen) {
      trackComponentEvent({
        event_name: 'show',
        component_type: 'pop_up_with_buttons',
        object,
        context: isMessageOwner ? 'message_owner_widget' : 'booking_widget',
      });
    }
  }, [isOpen]);

  const toggling = () => {
    setModalTitleBackDrop?.(!isOpen);
    setIsOpen(!isOpen);

    if (!isOpen) {
      trackComponentEvent({
        event_name: 'click',
        component_type: 'pop_up_with_buttons',
        object,
        context: isMessageOwner ? 'message_owner_widget' : 'booking_widget',
      });

      rudderTrack('selection_component_interaction', {
        action: 'start',
        name,
      });
    }
  };

  const onOptionClicked = (optionValue) => () => {
    trackComponentEvent({
      event_name: 'click',
      component_type: 'button',
      object,
      context: 'booking_widget',
      object_properties: {
        value: optionValue,
      },
    });

    rudderTrack('selection_component_interaction', {
      action: 'value_selected',
      name,
      value: optionValue,
    });
    setter(optionValue);
    setModalTitleBackDrop?.(false);
    setIsOpen(false);
    if (onChange) {
      onChange(optionValue);
    }
  };

  const wrapperRef = useRef(null);
  useOnClickOutside(wrapperRef, () => {
    if (!isOpen) return;
    setModalTitleBackDrop?.(false);
    setIsOpen(false);
  });

  return (
    <Main
      modalVersion={modalVersion}
      ref={wrapperRef}
      className={`
      ${isOpen ? 'isOpen' : ''}
      ${error ? 'hasError' : ''}
      ${isDisabled ? 'isDisabled' : ''}
      ${smallScreen ? 'smallScreen' : ''}
      ${className}
      `}
    >
      <DropDownContainer>
        <DropDownHeader
          onClick={toggling}
          data-testid={`components-forms-duration-picker-${title?.replace(' ', '-')}`}
          modalVersion={modalVersion}
        >
          <FlexCol>
            {value?.label ? (
              <ValueContainer
                data-testid={`components-forms-duration-picker-${title?.replace(' ', '-')}-flex`}
                modalVersion={modalVersion}
              >
                <SmallLabel>{title}</SmallLabel>
                <SelectInputValue modalVersion={modalVersion}>{value.label}</SelectInputValue>
              </ValueContainer>
            ) : (
              <Placeholder modalVersion={modalVersion}>{title}</Placeholder>
            )}
          </FlexCol>

          <SelectIconWrapper icon={!!icon} modalVersion={modalVersion} className={isOpen && 'open'}>
            {icon || <Arrow />}
          </SelectIconWrapper>
        </DropDownHeader>
        {error && <ErrorMessage modalVersion={modalVersion}>{error}</ErrorMessage>}

        {smallScreen && isOpen && (
          <>
            <BGOverlay />
            <MobileWrapper height={height} ref={mobileRef}>
              <MobileHeader>
                <span>{title}</span>

                <CloseWrapper
                  onClick={() => {
                    setModalTitleBackDrop?.(false);
                    setIsOpen(false);
                  }}
                >
                  <CloseIcon />
                </CloseWrapper>
              </MobileHeader>
              {notice && <div>{notice}</div>}
              <DropDownListContainerMobile>
                <DropDownListMobile ref={scrollDownRef}>
                  {options.map((option) => (
                    <ListItemWrapper
                      isOwner={isOwner}
                      className={`
                    ${option.value === value?.value ? 'selected' : ''}
                    ${option.available === false ? 'notAvailable' : ''}
                    `}
                      key={Math.random()}
                    >
                      <ListItem
                        onClick={onOptionClicked(option)}
                        className={`
                    ${option.available === false ? 'notAvailable' : ''}
                    `}
                      >
                        {option.label}
                      </ListItem>
                    </ListItemWrapper>
                  ))}
                </DropDownListMobile>
              </DropDownListContainerMobile>
            </MobileWrapper>
          </>
        )}
        {isOpen && !smallScreen && (
          <DropDownListContainer>
            {notice && <div>{notice}</div>}
            <DropDownList ref={scrollDownRef} noPaddingTop={!!notice}>
              {options.map((option) => (
                <ListItemWrapper
                  isOwner={isOwner}
                  data-testid={`components-forms-duration-${option?.label?.replace(' ', '-')}-option`}
                  className={`
                    ${option?.value === value?.value ? 'selected' : ''}
                    ${option?.available === false ? 'notAvailable' : ''}
                `}
                  key={Math.random()}
                >
                  <ListItem
                    onClick={onOptionClicked(option)}
                    className={`
                    ${option.available === false ? 'notAvailable' : ''}
                    `}
                  >
                    {option.label}
                  </ListItem>
                </ListItemWrapper>
              ))}
            </DropDownList>
          </DropDownListContainer>
        )}
      </DropDownContainer>
    </Main>
  );
};

const ErrorMessage = styled.div<{ modalVersion: boolean }>`
  position: absolute;
  ${({ modalVersion }) =>
    modalVersion &&
    css`
      position: unset;
    `}
  padding-top: 8px;
  font-weight: 500;
  font-size: 12px;
  color: #bc350a;
  bottom: 0;
`;

export const SelectIconWrapper = styled.div<{ modalVersion: boolean; icon: boolean }>`
  margin-left: auto;
  margin-right: 0;
  ${({ modalVersion }) =>
    modalVersion
      ? css`
          display: flex;
          margin-right: 12px;
        `
      : css`
          height: 17px;
        `}}

  ${mediaMMdMax} {
    display: flex;
  }

  ${({ icon }) =>
    !icon &&
    css`
      &.open {
        transform: rotate(180deg);
      }
    `}
`;

const Arrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
    <path
      d="M6 7L10.8619 1.85762C11.046 1.66294 11.046 1.3407 10.8619 1.14601C10.6779 0.951329 10.3732 0.951329 10.1891 1.14601L6 5.57678L1.81085 1.14601C1.62679 0.951329 1.32212 0.951329 1.13805 1.14601C0.953983 1.3407 0.953983 1.66294 1.13805 1.85762L6 7Z"
      fill="#0E7873"
    />
    <path
      d="M6 7L5.63668 7.34351C5.73112 7.4434 5.86253 7.5 6 7.5C6.13747 7.5 6.26888 7.4434 6.36332 7.34351L6 7ZM10.8619 1.85762L10.4986 1.51411L10.8619 1.85762ZM10.1891 1.14601L9.82583 0.802506L10.1891 1.14601ZM6 5.57678L5.63668 5.92029L6 6.30457L6.36332 5.92029L6 5.57678ZM1.81085 1.14601L2.17418 0.802506V0.802506L1.81085 1.14601ZM1.13805 1.85762L0.774731 2.20113L1.13805 1.85762ZM6.36332 7.34351L11.2253 2.20113L10.4986 1.51411L5.63668 6.65649L6.36332 7.34351ZM11.2253 2.20113C11.5916 1.8137 11.5916 1.18994 11.2253 0.802506L10.4986 1.48952C10.4971 1.48785 10.4972 1.48728 10.498 1.48936C10.4989 1.49177 10.5 1.49614 10.5 1.50182C10.5 1.50749 10.4989 1.51187 10.498 1.51428C10.4972 1.51635 10.4971 1.51578 10.4986 1.51411L11.2253 2.20113ZM11.2253 0.802506C10.8439 0.399165 10.2072 0.399165 9.82583 0.802506L10.5525 1.48952C10.5507 1.49135 10.547 1.49439 10.5414 1.49675C10.5362 1.49895 10.5308 1.5 10.5255 1.5C10.5203 1.5 10.5149 1.49895 10.5097 1.49675C10.5041 1.49439 10.5004 1.49135 10.4986 1.48952L11.2253 0.802506ZM9.82583 0.802506L5.63668 5.23328L6.36332 5.92029L10.5525 1.48952L9.82583 0.802506ZM6.36332 5.23328L2.17418 0.802506L1.44753 1.48952L5.63668 5.92029L6.36332 5.23328ZM2.17418 0.802506C1.79283 0.399165 1.15608 0.399165 0.774731 0.802506L1.50137 1.48952C1.49964 1.49135 1.49588 1.49439 1.49034 1.49675C1.48515 1.49895 1.47972 1.5 1.47445 1.5C1.46918 1.5 1.46376 1.49895 1.45857 1.49675C1.45303 1.49439 1.44926 1.49135 1.44753 1.48952L2.17418 0.802506ZM0.774731 0.802506C0.408423 1.18994 0.408423 1.8137 0.774731 2.20113L1.50137 1.51411C1.50295 1.51578 1.50277 1.51635 1.50198 1.51428C1.50107 1.51187 1.5 1.50749 1.5 1.50182C1.5 1.49614 1.50107 1.49177 1.50198 1.48936C1.50277 1.48728 1.50295 1.48785 1.50137 1.48952L0.774731 0.802506ZM0.774731 2.20113L5.63668 7.34351L6.36332 6.65649L1.50137 1.51411L0.774731 2.20113Z"
      fill="#0E7873"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.4568 0.67899C10.6954 0.440337 11.0824 0.440337 11.321 0.67899C11.5597 0.917644 11.5597 1.30458 11.321 1.54323L1.54323 11.321C1.30458 11.5597 0.917644 11.5597 0.67899 11.321C0.440337 11.0824 0.440337 10.6954 0.67899 10.4568L10.4568 0.67899Z"
      fill="#5E696A"
      stroke="#5E696A"
      strokeWidth="0.4"
    />
    <path
      d="M11.321 10.4568C11.5597 10.6954 11.5597 11.0824 11.321 11.321C11.0824 11.5597 10.6954 11.5597 10.4568 11.321L0.67899 1.54323C0.440337 1.30458 0.440337 0.917644 0.67899 0.67899C0.917644 0.440337 1.30458 0.440337 1.54323 0.67899L11.321 10.4568Z"
      fill="#5E696A"
      stroke="#5E696A"
      strokeWidth="0.4"
    />
  </svg>
);

export const SelectInputValue = styled.div<{ modalVersion: boolean }>`
  font-size: 16px;
  color: #17233c;
  font-weight: 600;
  ${({ modalVersion }) =>
    modalVersion &&
    css`
      margin-top: 1px;
    `}
`;

const Main = styled.div<{ modalVersion: boolean }>`
  border-radius: 6px !important;
  position: relative;

  ${({ modalVersion }) =>
    modalVersion &&
    css`
      ${mediaMMdMax} {
        position: unset;
      }
      border: 1px solid #cdd1d2;
      &.hasError {
        margin-bottom: 20px;
      }

      ${ErrorMessage} {
        bottom: -20px;
      }

      &.isOpen {
        border: solid 1px #8e9697 !important;
      }
    `}

  &.isDisabled {
    cursor: not-allowed;
    ${SelectInputValue} {
      color: #8e9697;
    }
    * {
      pointer-events: none;
    }
  }

  &.hasError {
    color: #bc350a;
    border-color: #bc350a;

    path,
    line {
      fill: #bc350a;
    }

    circle,
    line {
      stroke: #bc350a;
    }
  }
`;

const DropDownContainer = styled.div`
  width: 100%;
  min-height: 55px;
`;

export const DropDownHeader = styled.div<{ modalVersion: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  ${({ modalVersion }) =>
    modalVersion
      ? css`
          min-height: 64px;
        `
      : css`
          min-height: 55px;
        `}
`;

const Placeholder = styled.div<{ modalVersion: boolean }>`
  font-size: 16px;
  padding: 12px 12px 12px 0;
  color: #5e696a;

  ${({ modalVersion }) =>
    modalVersion &&
    css`
      padding: 12px;
    `}
`;

export const ValueContainer = styled.div<{ modalVersion: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ modalVersion }) => {
    if (modalVersion) {
      return css`
        padding: 12px 12px 8px 12px;
      `;
    }

    return css`
      padding-bottom: 14px;
      gap: 2px;
    `;
  }}
`;

export const SmallLabel = styled.div`
  color: #5e696a;
  font-size: 12px;
`;

const DropDownListContainer = styled.div`
  position: absolute;
  border-radius: 10px;
  width: 100%;
  z-index: 999;
  top: 55px;
  background-color: white;
  box-shadow: 0 0 10px rgb(0 0 0 / 25%) !important;
  border: 2px solid #e5e5e5;
  box-sizing: border-box;
`;

const DropDownListContainerMobile = styled.div`
  width: 100%;
  background-color: white;
  overflow: auto;
  height: 80%;
`;

const DropDownList = styled.ul<{ noPaddingTop: boolean }>`
  margin: 0;
  border-radius: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;

  background: #ffffff;
  color: #3faffa;
  max-height: 350px;
  overflow-y: auto;
  padding: 15px 15px 15px 15px;

  ${({ noPaddingTop }) =>
    noPaddingTop &&
    css`
      padding-top: 0;
    `}
`;

const DropDownListMobile = styled.ul`
  margin: 0;
  color: #3faffa;
  max-height: 100%;
  overflow-y: auto;
  padding: 25px;

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ListItemWrapper = styled.li<{ isOwner: boolean }>`
  list-style: none;
  font-size: 14px;
  flex: 45%;
  color: black;
  line-height: 1.6;
  font-weight: 600;
  border-radius: 4px;

  height: 55px;
  max-width: 140px;
  border: 1px solid #dbdfe5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(219, 223, 229, 0.5);
    border: 1px solid rgb(203, 207, 212);
  }

  &.selected {
    color: #0751c2;
    border: none;
    background: #eef3fb;
    ${({ isOwner }) =>
      isOwner &&
      css`
        border: 1px solid #0e7873;
        color: #0e7873;
        background: #eefbf9;
      `}
  }

  &.notAvailable {
    background-color: white !important;
    color: #dbdfe5 !important;
    border: 1px solid #dbdfe5 !important;
    cursor: not-allowed !important;
  }

  ${mediaMMdMax} {
    max-width: unset;
    flex: unset;
  }
`;

const ListItem = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  justify-content: center;
  padding: 3px 10px;

  &.notAvailable {
    pointer-events: none;
  }
`;

const MobileHeader = styled.div`
  font-weight: 600;
  color: rgb(23, 35, 60);
  padding: 24px 24px 12px;
  font-size: 20px;
  display: flex;
  align-items: center;
  background: white;
  z-index: 100;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;

  ${mediaMMdMax} {
    width: 100%;
  }
`;

const CloseWrapper = styled.div`
  background: #f5f7f9;
  display: flex;
  cursor: pointer;
  width: fit-content;
  padding: 10px;
  border-radius: 50%;
  margin-left: auto;
`;

const BGOverlay = styled.div`
  position: absolute;
  left: 0;
  background: rgba(10, 37, 47, 0.6);
  opacity: 1;
  z-index: 100000;
  width: 100%;
  height: 200%;
  top: 0;
`;

const MobileWrapper = styled.div<{ height: string }>`
  position: fixed;
  bottom: 0;
  background: white;
  width: 100vw;
  left: 0;
  height: ${({ height }) => height};
  z-index: 100000;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

export default BookingSelectInput;
